import { type TimelineFeatures, useFeatures } from "@expert/features";
import { Container } from "@mantine/core";
import { useIntersection } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useTimelineStore } from "../../state";
import { isFromBot, useAutoscroll } from "../../utils";
import { BotIcon } from "../bot-icon";
import { Feedback } from "../feedback-loader";
import { MessageBubble, MessageBubbleHeader, MessageBubbleWrapper } from "../message-bubble";
import { QuickActionGroup, useQuickActionsManager } from "../quick-actions";
import { MessageTag, TextMask } from "./elements";
import { Separator } from "./Separator";
import timelineClasses from "./TimelineItem.module.css";
import { TimelineItemWrapper } from "./TimelineItemWrapper";
import type { TimelineItemProps } from "./types";

export function TimelineItem({
    isLoading,
    isExternal = false,
    isLastBotMessage,
    isLastProactiveMessage,
    message,
    type,
    logger,
    scrollRef,
    metadata,
    children,
    gaiaPayloadProps,
}: TimelineItemProps) {
    const { ref: messageIntersectionRef, entry } = useIntersection({
        root: scrollRef?.current,
        threshold: 1,
    });
    const { start: startAutoscroll } = useAutoscroll(scrollRef);

    const overflowRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);

    const { getTimelineState, setIsMessageUnread, currentSessionId } = useTimelineStore();
    const { callSid } = getTimelineState(currentSessionId);

    const {
        features: { isProactiveEnabled },
    } = useFeatures<TimelineFeatures>();

    const { tag } = message ?? {};

    const isFromAI = isFromBot(type);

    const isProactiveMessage = isProactiveEnabled && type === "Proactive";
    const shouldShowProactive = isProactiveMessage && isLastProactiveMessage;
    const isBotListening = isProactiveEnabled && !isLoading && isLastBotMessage && !!callSid;

    let shouldShowExpanded = true;
    if (isLastProactiveMessage && !isExpanded && isOverflowing) {
        shouldShowExpanded = false;
    }

    const shouldShowMessageTag = tag && isLastProactiveMessage;
    const shouldShowFeedback =
        (type === "Search" || type === "SessionSummary") && shouldShowExpanded && message?.status !== "deactivated";

    const actions = useQuickActionsManager({
        currentMessageType: type,
        messageId: message?.id,
        isOverflowing,
        isExpanded: shouldShowExpanded,
        isLastBotMessage,
        isLastProactiveMessage,
        setIsExpanded,
    });
    const [isQuickActionsVisible, setIsQuickActionsVisible] = useState<boolean>(actions.length > 0);

    // remove cta buttons if the actions are empty
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!(actions.length > 0)) {
            setIsQuickActionsVisible(false);
        }
    }, [actions, setIsQuickActionsVisible]);

    // check if the proactive bot message is overflowing in the bubble
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        setIsOverflowing(
            overflowRef.current?.scrollHeight
                ? overflowRef.current.scrollHeight > overflowRef.current.clientHeight
                : false,
        );
    }, [overflowRef.current?.scrollHeight, overflowRef.current?.clientHeight]);

    /* Only autoscroll when expanding a message and NOT if a proactive
       message comes in while the expert is scrolled up in the timeline */
    useEffect(() => {
        if (shouldShowExpanded && isOverflowing && isLastBotMessage && !message?.isUnread) {
            startAutoscroll();
        }
    }, [shouldShowExpanded, isOverflowing, isLastBotMessage, message?.isUnread, startAutoscroll]);

    useEffect(() => {
        if (entry?.isIntersecting && message?.isUnread) {
            setIsMessageUnread(currentSessionId, message.id, false);
        }
    }, [entry?.isIntersecting, message?.isUnread, message?.id, setIsMessageUnread, currentSessionId]);

    // TODO: Clean up as part of TimelineItem refactor
    const messageBubbleWrapperContainer = timelineClasses.messageBubbleAndHeaderWrapper;
    const containerClassName =
        type === "SessionSummary" || type === "GuideRecommendation"
            ? `${timelineClasses.aiGeneratedContainer} ${messageBubbleWrapperContainer}`
            : messageBubbleWrapperContainer;

    return (
        <TimelineItemWrapper isFromAi={isFromAI}>
            <BotIcon isBotListening={isBotListening} isHidden={!isLastBotMessage && !isLoading} isLoading={isLoading} />
            {type === "Separator" ? (
                <Separator label={message?.text} />
            ) : (
                !isLoading && (
                    <Container className={containerClassName}>
                        <MessageBubbleHeader
                            isLastProactiveMessage={isLastProactiveMessage}
                            type={type}
                            metadata={metadata}
                        />
                        <MessageBubbleWrapper
                            isBotListening={isBotListening}
                            isExpanded={shouldShowExpanded}
                            type={type}
                        >
                            <MessageBubble
                                shouldShowProactive={shouldShowProactive}
                                isLastProactiveMessage={isLastProactiveMessage}
                                overflowRef={overflowRef}
                                messageIntersectionRef={messageIntersectionRef}
                                tag={tag}
                                type={type}
                            >
                                <TextMask isExpanded={shouldShowExpanded}>
                                    {shouldShowMessageTag && <MessageTag tagName={tag} />}
                                    {children}
                                </TextMask>
                                {isQuickActionsVisible && (
                                    <QuickActionGroup
                                        actions={actions}
                                        gaiaPayloadProps={gaiaPayloadProps}
                                        scrollRef={scrollRef}
                                        setIsQuickActionsVisible={setIsQuickActionsVisible}
                                        logger={logger}
                                        isExternal={isExternal}
                                    />
                                )}
                                {shouldShowFeedback && <Feedback message={message} />}
                            </MessageBubble>
                        </MessageBubbleWrapper>
                    </Container>
                )
            )}
        </TimelineItemWrapper>
    );
}
